import React, { useState } from 'react';
import {
  Navigate,
  Routes,
  Route,
  AppendNavigate,
  Outlet,
} from '@common/components';
import { PrivateRoute } from '@routes/private-route';
import {
  getFirstVisibleMenuItemRedirectTo,
  ORGANIZATION_ADMIN_MENU,
  OrganizationAdminVerticalNavMenu,
} from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu/organization-admin-vertical-nav-menu';
import { OnboardingListTemplatesPage } from 'works/legacy/pages/organization/admin-settings/templates-page/onboarding-list-templates';
import { OnboardingListTemplateNewPage } from 'works/legacy/pages/organization/admin-settings/templates-page/onboarding-list-templates/onboarding-list-template-new';
import { OnboardingListsTemplateEditPage } from 'works/legacy/pages/organization/admin-settings/templates-page/onboarding-list-templates/onboarding-lists-template-edit';
import { StaffingTemplatesPage } from 'works/legacy/pages/organization/admin-settings/templates-page/staffing-templates';
import { ApprovalsPage } from '@approvals/pages/organization/approvals-page';
import { OnboardingListsTemplateItemsPage } from 'works/legacy/pages/organization/admin-settings/templates-page/onboarding-list-templates/onboarding-lists-template-items';
import { OnboardingListsTemplateItemsConfirmUpdatePage } from 'works/legacy/pages/organization/admin-settings/templates-page/onboarding-list-templates/onboarding-lists-template-items/onboarding-lists-template-items-confirm-update-page';
import {
  AdminOrganizationAddUnitsPage,
  AdminOrganizationFacilityPage,
  AdminOrganizationStructurePage,
  AdminOrganizationUnitPage,
  AdminRatesLongTermPage,
  AdminSuppliersManagementPage,
  RatesLongTermCreationSteps,
  SuppliersManagementBulkLocationsUpdateSteps,
  SuppliersManagementInvitationSteps,
  SuppliersManagementLocationsUpdateSteps,
  SuppliersManagementNewSupplier,
} from '@institutions/pages/organization';
import { AdminTimesheetHolidays } from '@timekeeping/pages/organization';
import { ComplianceLinkAssignments } from '@onboarding/pages/compliance-link-assignments';
import {
  AdminAllowListsPage,
  AdminIncentiveRatesPage,
  AdminWorkforceGroupPage,
  AdminWorkforceGroupsPage,
  IncentivesUpdateSteps,
} from '@scheduling/pages/organization';
import { AdminOrganizationBillingPage } from '@pay-bill/pages/organization/admin-organization-billing-page';
import {
  AdminMembersPage,
  MembersAddEditSteps,
  MembersAddMultipleSteps,
  MembersUpdateLocationsSteps,
  FourZeroFourPage,
} from '@identity/pages';
import { useCurrentMembership } from '@identity/hooks';

const permissionKeys = [
  'canShowAdminApprovalsItem',
  'canShowAdminStaffingRequestTemplates',
  'canShowAdminWorksListTemplates',
  'canShowAdminRatesOnDemandTab',
  'canShowAdminRatesLongTermTab',
  'canShowAdminMembers',
  'canShowAdminSuppliers',
  'canShowAdminOrganizationStructure',
  'canShowAdminTimekeepingHolidays',
  'canShowAdminGroupsTab',
  'canShowAdminBilling',
  'canShowOrganizationOnDemandSettings',
] as const;

type PermissionKey = (typeof permissionKeys)[number];

type PermissionsSet = Record<PermissionKey, boolean>;

export function OrganizationAdminSettingsRoutes() {
  const [showMenu, setShowMenu] = useState(true);
  const currentMembership = useCurrentMembership();

  const permissions = permissionKeys.reduce((acc, key) => {
    acc[key] = currentMembership[key] ?? false;
    return acc;
  }, {} as PermissionsSet);

  const defaultLandingPagePath = getFirstVisibleMenuItemRedirectTo(permissions);

  const defaultRatesPath = permissions.canShowAdminRatesLongTermTab
    ? ORGANIZATION_ADMIN_MENU.RATES__LONG_TERM.ROUTE
    : ORGANIZATION_ADMIN_MENU.RATES__INCENTIVES.ROUTE;

  const defaultTemplatePath = permissions.canShowAdminStaffingRequestTemplates
    ? ORGANIZATION_ADMIN_MENU.TEMPLATES__STAFFING_REQUEST.ROUTE
    : ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE;

  return (
    // Nested sub-router: use plain Routes (not SentryRoutes) to avoid
    // last-writer-wins transaction name conflicts. See react-route.tsx for details.
    <Routes>
      <Route
        element={
          <OrganizationAdminVerticalNavMenu
            permissions={permissions}
            hideMenu={!showMenu}
          >
            {/* All the nested routes defined down below will be rendered by
             /* this Outlet component, as child of the 'works/admin' route */}
            <Outlet />
          </OrganizationAdminVerticalNavMenu>
        }
      >
        {/* DEFAULT LANDING PAGE */}

        <Route
          path="/"
          element={<Navigate to={defaultLandingPagePath} replace />}
        />

        {/* ALLOW LISTS */}

        <Route
          path="/allow-lists"
          element={
            <PrivateRoute
              menuItem="admin"
              page={AdminAllowListsPage}
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowOrganizationOnDemandSettings}
            />
          }
        />

        {/* TEMPLATES */}

        <Route
          path="/templates"
          element={<Navigate to={defaultTemplatePath} replace />}
        />
        <Route
          path="/templates/staffing-request"
          element={
            <PrivateRoute
              menuItem="admin"
              page={StaffingTemplatesPage}
              secondaryMenuItem="staffing-request-templates"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminStaffingRequestTemplates}
            />
          }
        />

        <Route
          path="/templates/compliance-list/:id/items/confirm-updates"
          element={
            <PrivateRoute
              page={OnboardingListsTemplateItemsConfirmUpdatePage}
              menuItem="admin"
              secondaryMenuItem="compliance-list-template-item-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminWorksListTemplates}
            />
          }
        />
        <Route
          path="/templates/compliance-list/:id/items"
          element={
            <PrivateRoute
              page={OnboardingListsTemplateItemsPage}
              menuItem="admin"
              secondaryMenuItem="compliance-list-template-item-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminWorksListTemplates}
            />
          }
        />
        <Route
          path="/templates/compliance-list/:id/edit"
          element={
            <PrivateRoute
              page={OnboardingListsTemplateEditPage}
              menuItem="admin"
              secondaryMenuItem="compliance-list-templates"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminWorksListTemplates}
            />
          }
        />
        <Route
          path="/templates/compliance-list/:id/link-assignments"
          element={
            <PrivateRoute
              page={ComplianceLinkAssignments}
              menuItem="admin"
              secondaryMenuItem="compliance-list-template-link-assignments-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminWorksListTemplates}
            />
          }
        />
        <Route
          path="/templates/compliance-list/new"
          element={
            <PrivateRoute
              page={OnboardingListTemplateNewPage}
              menuItem="admin"
              secondaryMenuItem="compliance-list-templates"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminWorksListTemplates}
            />
          }
        />
        <Route
          path="/templates/compliance-list"
          element={
            <PrivateRoute
              page={OnboardingListTemplatesPage}
              menuItem="admin"
              secondaryMenuItem="compliance-list-templates"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminWorksListTemplates}
            />
          }
        />

        {/* LOCATIONS */}

        <Route
          path="/locations"
          element={
            <PrivateRoute
              menuItem="admin"
              page={AdminOrganizationStructurePage}
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminOrganizationStructure}
            />
          }
        />

        <Route
          path="/locations/add-units"
          element={
            <PrivateRoute
              page={AdminOrganizationAddUnitsPage}
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminOrganizationStructure}
            />
          }
        />

        <Route
          path="/locations/unit/:organizationUnitId"
          element={<AppendNavigate append="unit-details" />}
        />
        <Route
          path="/locations/unit/:organizationUnitId/:tabKey" // tabKey: unit-details | shifts | integration | shift-tags
          element={
            <PrivateRoute
              menuItem="admin"
              page={AdminOrganizationUnitPage}
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminOrganizationStructure}
            />
          }
        />

        <Route
          path="/locations/facility/:organizationFacilityId"
          element={<AppendNavigate append="details" />}
        />
        <Route
          path="/locations/facility/:organizationFacilityId/:page" // page: details | groups
          element={
            <PrivateRoute
              menuItem="admin"
              page={AdminOrganizationFacilityPage}
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminOrganizationStructure}
            />
          }
        />

        {/* WORKFORCE GROUPS */}

        <Route
          path="/groups"
          element={
            <PrivateRoute
              page={AdminWorkforceGroupsPage}
              menuItem="admin"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminGroupsTab}
            />
          }
        />

        <Route
          path="/groups/:workforceGroupId"
          element={<AppendNavigate append="details" />}
        />
        <Route
          path="/groups/:workforceGroupId/:tabKey" // tabKey: details | staff | locations | rates
          element={
            <PrivateRoute
              page={AdminWorkforceGroupPage}
              menuItem="admin"
              secondaryMenuItem="workforce-group-details"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminGroupsTab}
            />
          }
        />

        {/* APPROVALS */}

        <Route
          path="/approvals/:organizationBusinessId?"
          element={
            <PrivateRoute
              page={ApprovalsPage}
              menuItem="admin"
              secondaryMenuItem="approvals-page"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminApprovalsItem}
            />
          }
        />

        {/* RATES */}

        <Route
          path="/rates"
          element={<Navigate to={defaultRatesPath} replace />}
        />

        <Route
          path="/rates/long-term"
          element={
            <PrivateRoute
              page={AdminRatesLongTermPage}
              menuItem="admin"
              secondaryMenuItem="rate-long-term-page"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminRatesLongTermTab}
            />
          }
        />
        <Route
          path="/rates/long-term/create"
          element={
            <PrivateRoute
              page={RatesLongTermCreationSteps}
              menuItem="admin"
              secondaryMenuItem="rate-creation-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminRatesLongTermTab}
            />
          }
        />

        <Route
          path="/rates/incentives"
          element={
            <PrivateRoute
              page={AdminIncentiveRatesPage}
              menuItem="admin"
              secondaryMenuItem="rates-on-demand-page"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminRatesOnDemandTab}
            />
          }
        />
        <Route
          path="/rates/incentives/update"
          element={
            <PrivateRoute
              page={IncentivesUpdateSteps}
              menuItem="admin"
              secondaryMenuItem="rates-on-demand-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminRatesOnDemandTab}
            />
          }
        />

        {/* SUPPLIERS */}

        <Route
          path="/suppliers"
          element={
            <PrivateRoute
              menuItem="admin"
              page={AdminSuppliersManagementPage}
              secondaryMenuItem="suppliers-page"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminSuppliers}
            />
          }
        />

        <Route
          path="/suppliers/new"
          element={
            <PrivateRoute
              menuItem="admin"
              page={SuppliersManagementNewSupplier}
              secondaryMenuItem="supplier"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminSuppliers}
            />
          }
        />

        <Route
          path="/suppliers/invitation/:supplierId?"
          element={
            <PrivateRoute
              menuItem="admin"
              page={SuppliersManagementInvitationSteps}
              secondaryMenuItem="supplier-invitation-steps"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminSuppliers}
            />
          }
        />

        <Route
          path="/suppliers/locations-update/:supplierId"
          element={
            <PrivateRoute
              menuItem="admin"
              page={SuppliersManagementLocationsUpdateSteps}
              secondaryMenuItem="supplier-permission-update-steps"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminSuppliers}
            />
          }
        />
        <Route
          path="/suppliers/activation/:supplierId"
          element={
            <PrivateRoute
              menuItem="admin"
              page={SuppliersManagementLocationsUpdateSteps}
              secondaryMenuItem="supplier-activation-steps"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminSuppliers}
            />
          }
        />
        <Route
          path="/suppliers/bulk-locations-update"
          element={
            <PrivateRoute
              menuItem="admin"
              page={SuppliersManagementBulkLocationsUpdateSteps}
              secondaryMenuItem="bulk-supplier-permission-update-steps"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminSuppliers}
            />
          }
        />

        {/* BILLING */}

        <Route
          path="/billing"
          element={
            <PrivateRoute
              menuItem="admin"
              page={AdminOrganizationBillingPage}
              secondaryMenuItem="billing-page"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminBilling}
            />
          }
        />

        {/* TIMEKEEPING */}

        <Route
          path="/timekeeping"
          element={
            <PrivateRoute
              menuItem="admin"
              page={AdminTimesheetHolidays}
              secondaryMenuItem="timesheets-page"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminTimekeepingHolidays}
            />
          }
        />

        {/* USERS / MEMBERS */}

        <Route
          path="/users"
          element={
            <PrivateRoute
              menuItem="admin"
              page={AdminMembersPage}
              secondaryMenuItem="members-page"
              onRender={() => setShowMenu(true)}
              permitted={permissions.canShowAdminMembers}
            />
          }
        />
        <Route
          path="/users/add"
          element={
            <PrivateRoute
              menuItem="admin"
              page={MembersAddEditSteps}
              secondaryMenuItem="members-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminMembers}
            />
          }
        />
        <Route
          path="/users/edit/:email"
          element={
            <PrivateRoute
              menuItem="admin"
              page={MembersAddEditSteps}
              secondaryMenuItem="members-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminMembers}
            />
          }
        />
        <Route
          path="/users/update-locations"
          element={
            <PrivateRoute
              menuItem="admin"
              page={MembersUpdateLocationsSteps}
              secondaryMenuItem="members-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminMembers}
            />
          }
        />
        <Route
          path="/users/upload"
          element={
            <PrivateRoute
              menuItem="admin"
              page={MembersAddMultipleSteps}
              secondaryMenuItem="members-page"
              onRender={() => setShowMenu(false)}
              permitted={permissions.canShowAdminMembers}
            />
          }
        />

        {/* 404 PAGE */}

        <Route path="*" element={<FourZeroFourPage />} />
      </Route>
    </Routes>
  );
}
